import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Droplets,
  Flame,
  Handshake,
  Home,
  Megaphone,
  Palette,
  ShieldCheck,
  Sofa,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import GuideDownloadSection from "@/components/sections/GuideDownloadSection";
import { SELLERS_GUIDE } from "@/lib/guides";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import Checklist from "@/components/sections/Checklist";
import SplitSection from "@/components/sections/SplitSection";
import CardGrid from "@/components/sections/CardGrid";
import ComparisonBlock from "@/components/sections/ComparisonBlock";
import TestimonialsSection from "@/components/TestimonialsSection";
import BuyerStrategySection from "@/components/sections/BuyerStrategySection";
import OverlayContentCard from "@/components/sections/OverlayContentCard";
import AccordionGroup from "@/components/sections/AccordionGroup";
import heroImage from "@/assets/sell-your-home-hero.jpg";
import sellStrategyGuidance from "@/assets/sell-strategy-guidance.jpg";
import sellGuidedExperience from "@/assets/sell-guided-experience.jpg";
import sellProcessConsultation from "@/assets/sell-process-consultation.jpg";
import sellPreparationStrategy from "@/assets/sell-preparation-strategy.jpg";
import sellSuccessFirstImpression from "@/assets/sell-success-first-impression.jpg";
import sellSuccessBetterOffers from "@/assets/sell-success-better-offers.jpg";
import sellSuccessSmoothTransition from "@/assets/sell-success-smooth-transition.jpg";
import sellReadyConsultation from "@/assets/sell-ready-consultation.jpg";
import sellFinalCta from "@/assets/sell-final-cta.jpg";

const sellerGuidedExperience = [
  {
    title: "Thoughtful preparation",
    description:
      "We identify the updates, repairs, and finishing touches that help your home show at its best.",
  },
  {
    title: "Pricing with strategy",
    description:
      "We position your home with real market context so pricing supports momentum from day one.",
  },
  {
    title: "Presentation that stands out",
    description:
      "Professional visuals and elevated marketing help your property command stronger attention.",
  },
  {
    title: "Negotiation with purpose",
    description:
      "From showings to offers, we guide every conversation with your outcome in mind.",
  },
];

const sellingSteps = [
  {
    label: "Step 01",
    title: "Consultation & home evaluation",
    description:
      "We assess your property, timing, and goals to identify what will strengthen your result before listing.",
  },
  {
    label: "Step 02",
    title: "Preparation & improvements",
    description:
      "We guide the cleaning, repairs, painting, and finishing touches that elevate buyer perception.",
  },
  {
    label: "Step 03",
    title: "Pricing & positioning",
    description:
      "We use real market context to price strategically and launch with clarity instead of guesswork.",
  },
  {
    label: "Step 04",
    title: "Listing & marketing launch",
    description:
      "Your home goes live with thoughtful presentation, strong visuals, and exposure designed to stand out.",
  },
  {
    label: "Step 05",
    title: "Showings, offers & closing",
    description:
      "We manage buyer interest, guide negotiations, and help you move through closing with confidence.",
  },
];

const sellerValues = [
  {
    icon: ShieldCheck,
    title: "Preparation-first mindset",
    description:
      "We focus on what improves buyer perception before your home ever reaches the market.",
  },
  {
    icon: TrendingUp,
    title: "Data-backed pricing",
    description:
      "A pricing strategy built to create momentum, protect value, and support stronger offers.",
  },
  {
    icon: Megaphone,
    title: "Elevated marketing",
    description:
      "Presentation, visuals, and messaging designed to help your home stand out immediately.",
  },
  {
    icon: Users,
    title: "Qualified buyer focus",
    description:
      "We position your home to attract the right attention, not just more traffic.",
  },
  {
    icon: Handshake,
    title: "Experienced negotiation",
    description:
      "You get guidance through offers and terms with a steady eye on your best outcome.",
  },
];

const sellerSuccesses = [
  {
    title: "A stronger first impression",
    description:
      "With the right updates and presentation, a seller launched with more confidence and stronger buyer interest.",
    image: sellSuccessFirstImpression,
  },
  {
    title: "Better offers through preparation",
    description:
      "Strategic improvements and sharper positioning helped the property attract more competitive offers.",
    image: sellSuccessBetterOffers,
  },
  {
    title: "A smoother transition",
    description:
      "Clear guidance from pricing through closing reduced stress and kept the sale moving with intention.",
    image: sellSuccessSmoothTransition,
  },
];

const sellerTestimonials = [
  {
    name: "Massachusetts Seller",
    text: "Preparing our home before listing made a huge difference. We felt more confident, the home showed beautifully, and the strategy behind every step was clear.",
  },
  {
    name: "Recent Seller",
    text: "Hadassah Homes brought structure, calm, and real expertise to the process. We never felt like we were just putting a house on the market and hoping for the best.",
  },
  {
    name: "Downsizing Client",
    text: "From the pricing strategy to the final negotiations, we felt supported the whole way through. The process was far more polished and less stressful than we expected.",
  },
];

const faqItems = [
  {
    title: "Should I make repairs before listing my home?",
    content:
      "Often, yes. Strategic updates and repairs can improve buyer perception, help your home show better, and support a stronger sales outcome.",
  },
  {
    title: "How do you determine the right listing price?",
    content:
      "We look at current market data, comparable properties, condition, presentation, and timing to position your home strategically.",
  },
  {
    title: "Do I need to stage my home to sell it well?",
    content:
      "Not always, but presentation matters. We help you determine what level of preparation, styling, or improvement will create the best return.",
  },
  {
    title: "What if I am not ready to list yet?",
    content:
      "That is completely fine. We can help you understand your home&apos;s value, identify next steps, and build a plan before you go to market.",
  },
];

const SellYourHome = () => {
  const [activeSellerValue, setActiveSellerValue] = useState(0);
  const [isSellerValuePaused, setIsSellerValuePaused] = useState(false);

  useEffect(() => {
    document.title = "Sell Your Home in Massachusetts | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Sell your home for its full potential. Hadassah Homes combines property preparation, strategic pricing, and expert marketing to help you sell with confidence."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/sell-your-home";
  }, []);

  useEffect(() => {
    if (isSellerValuePaused) return;

    const interval = setInterval(() => {
      setActiveSellerValue((prev) => (prev + 1) % sellerValues.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isSellerValuePaused]);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Sell Your Home"
        title="Sell Your Home in Massachusetts"
        subtitle="A more strategic way to prepare, position, and sell for your home&apos;s full potential."
        backgroundImage={heroImage}
      />

      <BuyerStrategySection
        subheading="Seller Strategy"
        heading="Selling well starts long before your listing goes live."
        description="Most homes do not underperform because of the market alone. They underperform because they were not prepared, positioned, or presented with enough strategy. Hadassah Homes combines property preparation, pricing insight, and elevated marketing to help you sell with more confidence and stronger results."
        image={sellStrategyGuidance}
        imageAlt="Diverse Massachusetts homeowners walking through a polished home with a real estate advisor"
        cta={{ label: "Book a Consultation", href: "/consultation" }}
        secondaryCta={{ label: "Property Preparation Services", href: "/property-preparation" }}
        features={[
          {
            icon: Target,
            title: "Clear prep priorities",
            description: "Know which improvements matter most before you spend time or money.",
          },
          {
            icon: TrendingUp,
            title: "Pricing strategy",
            description: "Position your home with stronger market context from the start.",
          },
          {
            icon: Sparkles,
            title: "Presentation that performs",
            description: "Create a sharper first impression online and in person.",
          },
          {
            icon: Handshake,
            title: "Stronger negotiations",
            description: "Launch with a plan that supports confidence through the offer stage.",
          },
        ]}
      />

      {/* Seller Perks */}
      <section className="relative py-20 md:py-28 bg-[#0a1510]">
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <div className="text-center mb-10 md:mb-14 space-y-3">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-accent">Included With Your Listing</p>
            <h2 className="font-headline text-3xl md:text-4xl text-white">What Hadassah Homes Covers for You</h2>
            <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              When you list with us, these services come standard — no extra cost, no chasing paperwork.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            <OverlayContentCard
              variant="glass"
              icon={Droplets}
              title="Water Certification"
              description="Paid by Hadassah Homes. We handle the water certification required at closing so you don't have to."
            />
            <OverlayContentCard
              variant="glass"
              icon={Flame}
              title="Smoke Certification"
              description="Paid by Hadassah Homes. We cover the smoke and CO detector certification required by Massachusetts law."
            />
            <OverlayContentCard
              variant="glass"
              icon={Palette}
              title="Design Consultation"
              description="Complimentary. Expert advice on the updates and finishes that will maximize your return before you list."
            />
            <OverlayContentCard
              variant="glass"
              icon={Sofa}
              title="Staging Consultation"
              description="Complimentary. Guidance to help your home stand out, photograph better, and create a stronger first impression."
            />
          </div>
        </div>
      </section>

      <ContentSection variant="dark">
        <div className="mx-auto max-w-wide grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-10 md:gap-14 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7 lg:pt-2">
            <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
              Guided Experience
            </span>
            <h2 className="font-headline text-4xl md:text-5xl lg:text-6xl text-secondary-foreground leading-tight">
              What It&apos;s Like to Sell With Us
            </h2>
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.8] max-w-xl">
              We do more than put your home on the market. We help you prepare it, position it,
              and present it in a way that supports a more polished, higher-trust selling
              experience from start to finish.
            </p>
            <div className="border-l-2 border-accent/60 pl-5">
              <p className="font-headline text-2xl text-secondary-foreground leading-snug">
                Strategic preparation, thoughtful marketing, and calm guidance at every step.
              </p>
            </div>
          </div>

          <div className="space-y-8 md:space-y-10">
            <div className="overflow-hidden border border-secondary-foreground/12">
              <img
                src={sellGuidedExperience}
                alt="Diverse Massachusetts seller reviewing a home preparation plan with an agent"
                className="h-[320px] w-full object-cover md:h-[420px]"
                loading="lazy"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 md:gap-y-10">
              {sellerGuidedExperience.map((item) => (
                <div key={item.title} className="space-y-4">
                  <div className="h-px w-14 bg-accent/55" aria-hidden="true" />
                  <div className="space-y-3">
                    <h3 className="font-headline text-[1.7rem] md:text-[1.95rem] text-secondary-foreground leading-[1.14]">
                      {item.title}
                    </h3>
                    <p className="text-secondary-foreground/80 text-base md:text-[1.02rem] leading-[1.8]">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ContentSection>

      <GuideDownloadSection
        guide={SELLERS_GUIDE}
        location="sell-your-home"
        heading="Selling Your Home? Start With Our Seller's Guide"
        body="Get a clear overview of what to expect when selling your home, from preparing your property and understanding the market to offers, negotiations, and closing."
      />

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-12 md:gap-16 items-start">
          <div className="lg:self-start">
            <div className="relative lg:sticky lg:top-32">
              <div className="relative">
                <div className="overflow-hidden lg:h-[min(74vh,780px)]">
                  <img
                    src={sellProcessConsultation}
                    alt="Massachusetts homeowner and agent reviewing a listing plan inside a bright home"
                    className="w-full h-full aspect-[4/5] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-6 right-6 md:right-8 bg-card border border-border shadow-xl p-5 max-w-[240px]">
                  <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-2">
                    Seller Process
                  </p>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    A structured selling path built to reduce guesswork and maximize preparation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <SectionHeader
              eyebrow="Process"
              title="The Selling Process, Simplified"
              description="We break the sale into clear stages so you know what matters first, what supports value, and how to move forward with confidence."
              align="left"
            />
            <div className="space-y-8 md:space-y-10">
              {sellingSteps.map((step, index) => (
                <div
                  key={step.title}
                  className="relative bg-card border border-border shadow-sm p-6 md:p-8 lg:min-h-[210px]"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent/85" aria-hidden="true" />
                  <div className="pl-3">
                    <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-3">
                      {step.label}
                    </p>
                    <h3 className="font-headline text-2xl md:text-3xl text-foreground mb-3 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-base md:text-lg leading-[1.8] max-w-2xl">
                      {step.description}
                    </p>
                  </div>
                  {index < sellingSteps.length - 1 && (
                    <div
                      className="hidden lg:block absolute left-[14px] -bottom-8 h-8 w-px bg-border"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="Preparation Is Part of the Strategy"
          description="Before your home hits the market, we help you refine what buyers see first and what they remember most."
          image={sellPreparationStrategy}
          imageAlt="Staged Massachusetts home interior with a homeowner and advisor discussing updates before listing"
        >
          <Checklist
            columns={2}
            items={[
              "Professional cleaning for a stronger first impression",
              "Carpentry and repairs that reduce friction",
              "Interior and exterior painting that refreshes the space",
              "Strategic updates that support value and appeal",
            ]}
          />
          <p className="text-secondary-foreground/82 text-base md:text-lg leading-relaxed pt-3">
            Our{" "}
            <Link
              to="/property-preparation"
              className="text-accent underline underline-offset-4 hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              property preparation services
            </Link>{" "}
            help sellers go to market with a sharper, more confident presentation.
          </p>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="space-y-5">
            <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
              Strategic Positioning
            </span>
            <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
              Why Homes Leave Money on the Table
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-[1.75]">
              The difference is rarely just luck. It usually comes down to preparation,
              pricing, presentation, and how well the property is positioned from the start.
            </p>
          </div>

          <ComparisonBlock
            rows={[
              "Pre-listing preparation",
              "Pricing approach",
              "Buyer perception",
              "Marketing quality",
              "Offer strength",
            ]}
            columns={[
              {
                title: "Typical Listing Approach",
                values: [
                  "Minimal or reactive",
                  "Estimated without strategy",
                  "Mixed first impression",
                  "Basic exposure",
                  "Less consistent",
                ],
              },
              {
                title: "With Hadassah Homes",
                highlighted: true,
                values: [
                  "Planned before launch",
                  "Market-aware and intentional",
                  "Polished and confidence-building",
                  "Elevated presentation",
                  "Positioned for stronger interest",
                ],
              },
            ]}
          />
        </div>
      </ContentSection>

      <section className="relative overflow-hidden bg-[#123B2A] py-20 md:py-24 lg:py-32">
        <div className="pointer-events-none absolute -right-24 top-12 h-72 w-72 bg-[radial-gradient(circle,rgba(201,169,92,0.14)_0%,rgba(201,169,92,0.06)_32%,transparent_72%)]" />
        <div className="pointer-events-none absolute -left-20 bottom-10 h-64 w-64 bg-[radial-gradient(circle,rgba(201,169,92,0.12)_0%,rgba(201,169,92,0.04)_34%,transparent_74%)]" />
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
            <div className="lg:sticky lg:top-32 lg:self-start">
              <div className="max-w-xl space-y-9 text-left">
                <p className="font-body text-xs uppercase tracking-[0.16em] text-[#C9A95C]">
                  Seller Value
                </p>
                <h2 className="font-headline text-3xl leading-[1.08] text-white md:text-[3.2rem]">
                  Why Sellers Choose Hadassah Homes
                </h2>
                <p className="max-w-lg text-base leading-[1.85] text-white/85 md:text-lg">
                  The experience is designed to feel more prepared, more polished, and more strategic from consultation to closing.
                </p>
                <p className="max-w-lg text-base leading-[1.8] text-white md:text-lg">
                  Whether you&apos;re selling to move into something new or preparing for your next
                  investment, we help you move through the process with clarity and stronger
                  positioning.
                </p>
                <div className="border border-[#C9A95C]/30 bg-white/[0.05] px-5 py-5">
                  <div className="flex items-start gap-4">
                    <span className="mt-1.5 h-9 w-px shrink-0 bg-[#C9A95C]" />
                    <p className="text-sm font-medium leading-[1.75] text-white/85 md:text-base">
                      A selling strategy built around preparation, presentation, and stronger buyer confidence.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="w-full max-w-none space-y-4 lg:pt-1"
              onMouseEnter={() => setIsSellerValuePaused(true)}
              onMouseLeave={() => setIsSellerValuePaused(false)}
            >
              {sellerValues.map((item, index) => (
                <div
                  key={item.title}
                  className={`relative overflow-hidden border px-6 py-6 transition-all duration-500 ${
                    activeSellerValue === index
                      ? "border-[#C9A95C]/45 bg-white/[0.09] shadow-[0_20px_45px_-34px_rgba(0,0,0,0.38)]"
                      : "border-white/15 bg-white/[0.045] hover:bg-white/[0.075]"
                  }`}
                >
                  {activeSellerValue === index ? (
                    <>
                      <span className="absolute left-0 top-0 h-full w-px bg-[#C9A95C]" />
                      <span className="absolute left-0 top-0 h-px w-full bg-[#C9A95C]/60" />
                    </>
                  ) : null}
                  <button
                    type="button"
                    aria-expanded={activeSellerValue === index}
                    aria-controls={`seller-value-${index}`}
                    onClick={() => setActiveSellerValue(index)}
                    className="flex w-full items-start justify-between gap-4 text-left"
                  >
                    <div className="flex min-w-0 items-start gap-4">
                      <div className="pt-1.5">
                        <span className="inline-flex items-center gap-3 font-body text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C9A95C]">
                          <span
                            className={`h-px w-7 transition-colors duration-300 ${
                              activeSellerValue === index ? "bg-[#C9A95C]" : "bg-[#C9A95C]/55"
                            }`}
                          />
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-headline text-[1.45rem] leading-tight text-white md:text-[1.72rem]">
                          {item.title}
                        </h3>
                      </div>
                    </div>
                    <ChevronDown
                      className={`mt-1.5 h-5 w-5 shrink-0 text-[#C9A95C] transition-transform duration-300 ${
                        activeSellerValue === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {activeSellerValue === index ? (
                    <div
                      id={`seller-value-${index}`}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 flex items-start gap-4 border-t border-white/10 pt-4">
                        <span className="mt-2 h-2 w-2 shrink-0 bg-[#C9A95C]" />
                        <p className="max-w-2xl text-base leading-[1.78] text-white/80 md:text-lg">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ContentSection>
        <SectionHeader
          eyebrow="Results"
          title="Real Results Come From Preparation"
          description="Proof points that show how thoughtful improvements and stronger positioning can change the selling experience."
        />
        <CardGrid items={sellerSuccesses} columns={3} />
      </ContentSection>

      <TestimonialsSection
        sectionId="seller-testimonials"
        title="What Our Sellers Say"
        testimonials={sellerTestimonials}
      />

      <ContentSection variant="dark">
        <div className="mx-auto max-w-[1440px] bg-[#173D2D] px-5 py-10 md:px-10 md:py-12 lg:px-14 lg:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:items-center">
            <div className="order-2 relative overflow-hidden min-h-[420px] lg:order-1 lg:min-h-[520px]">
              <img
                src={sellReadyConsultation}
                alt="Massachusetts homeowner speaking with a real estate advisor about home value and next steps"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
              <div className="relative flex min-h-[420px] items-end p-5 md:p-8 lg:min-h-[520px] lg:p-10">
                <div className="grid w-full max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="border border-white/15 bg-black/35 p-5 text-white backdrop-blur-md">
                    <Home className="mb-3 h-5 w-5 text-[#C9A95C]" strokeWidth={1.8} />
                    <p className="font-headline text-lg text-white">
                      Understand value
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-white/80">
                      Get clearer on pricing, condition, and timing before you commit.
                    </p>
                  </div>
                  <div className="border border-white/15 bg-black/35 p-5 text-white backdrop-blur-md">
                    <ShieldCheck className="mb-3 h-5 w-5 text-[#C9A95C]" strokeWidth={1.8} />
                    <p className="font-headline text-lg text-white">
                      Plan next steps
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-white/80">
                      Know what to improve, what to skip, and how to move forward confidently.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 text-left lg:order-2">
              <div className="max-w-xl space-y-6">
                <span className="font-body text-xs uppercase tracking-[0.14em] text-[#C9A95C]">
                  Next Step Clarity
                </span>
                <h2 className="font-headline text-4xl leading-tight text-white md:text-5xl">
                  Not Sure If You&apos;re Ready to Sell Yet?
                </h2>
                <p className="max-w-xl text-base leading-[1.8] text-white/85 md:text-lg">
                  You do not need to have every detail figured out before reaching out. We can
                  help you understand your home&apos;s value, identify the right preparation
                  priorities, and build a plan before you list.
                </p>
                <div className="pt-1">
                  <Link
                    to="/consultation"
                    className="inline-flex items-center gap-2 bg-[#C9A95C] px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-black transition-colors hover:bg-[#b8954f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A95C]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#173D2D]"
                  >
                    Book a Consultation
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <p className="max-w-lg text-sm leading-relaxed text-white/70 md:text-base">
                  No pressure. Just a clear conversation about your timing, goals, and next best move.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Frequently Asked Questions"
          description="Straightforward answers for sellers preparing to bring their home to market."
        />
        <div className="max-w-3xl mx-auto">
          <AccordionGroup items={faqItems} />
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={sellFinalCta}
          alt="Beautiful Massachusetts home exterior at golden hour with subtle human presence"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-black/30" />
        <div className="relative z-20 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-white">
              Let&apos;s Maximize Your Home&apos;s Value
            </h2>
            <p className="mt-6 text-white/90 text-base md:text-lg leading-relaxed">
              Selling your home is a major decision. We combine preparation, pricing strategy, and market expertise to help you sell with more confidence and a stronger plan.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consultation"
                className="inline-flex items-center justify-center gap-2 font-body text-sm uppercase tracking-[0.12em] px-10 py-4 bg-accent text-white hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black/70"
              >
                Book a Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/property-preparation"
                className="inline-flex items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black/70"
              >
                Explore Property Prep
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default SellYourHome;
